// ============================================================================
// /people/directory — ONE directory with ONE card for every person TCS works
// with: salaried, hourly, 1099 individuals and sub owners (Ben, 2026-10-06,
// item 818). It replaces the salaried-only Employees directory, whose old
// paths land here.
//
// One search box (name or phone). Type filters: Everyone · Hourly · Salaried ·
// 1099 · Subs · No phone · Separated. Everything is read from
// v_people_directory; nothing is merged — a person with two records shows
// both "hats" on one row and one card, joined only by the link fields that
// already exist (decision 1A).
//
// "No phone" is the PIN gap: an active hourly worker with no mobile number.
// Its count is the live count, not a figure anybody wrote down.
// ============================================================================
import { canWriteHrRecord } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Cake, CalendarHeart, Phone, PhoneOff, Search, UserPlus, Users } from 'lucide-react';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  useToast,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { BirthdaysModal } from '../hr-onboarding';
import EmployeeCreateModal from './employee-create-modal';
import {
  DIRECTORY_FILTERS,
  PERSON_TYPE_LABELS,
  matchesFilter,
  matchesSearch,
  usePeopleDirectory,
  type DirectoryFilter,
  type Person,
  type PersonRow,
  type PersonType,
} from './hooks/use-people-directory';


function isFilter(v: string | null): v is DirectoryFilter {
  return !!v && DIRECTORY_FILTERS.some((f) => f.key === v);
}

export default function PeopleDirectory() {
  const { profile } = useAuth();
  const toast = useToast();
  const canWrite = canWriteHrRecord(profile);
  const [params, setParams] = useSearchParams();
  const filter: DirectoryFilter = isFilter(params.get('filter')) ? (params.get('filter') as DirectoryFilter) : 'everyone';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [createOpen, setCreateOpen] = useState(false);
  const [modal, setModal] = useState<'birthday' | 'anniversary' | null>(null);
  const { people, loading, error, refetch } = usePeopleDirectory();

  const counts = useMemo(() => {
    const out = {} as Record<DirectoryFilter, number>;
    for (const f of DIRECTORY_FILTERS) out[f.key] = people.filter((p) => matchesFilter(p, f.key)).length;
    return out;
  }, [people]);

  const shown = useMemo(
    () => people.filter((p) => matchesFilter(p, filter) && matchesSearch(p, search)),
    [people, filter, search],
  );

  function setFilter(next: DirectoryFilter) {
    const p = new URLSearchParams(params);
    if (next === 'everyone') p.delete('filter');
    else p.set('filter', next);
    setParams(p, { replace: true });
  }

  if (error) return <ErrorState title="Couldn't load the directory" body={error} />;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow="People · everyone"
        title="People"
        meta={loading ? 'Loading…' : `${shown.length} of ${counts.everyone} on a roster · ${counts.separated} separated`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canWrite && (
              <Button
                variant="primary"
                size="sm"
                icon={<UserPlus className="h-3.5 w-3.5" />}
                onClick={() => setCreateOpen(true)}
              >
                Add employee
              </Button>
            )}
            {/* The old HR tab's birthday and anniversary lists, unchanged, kept reachable. */}
            <Button variant="secondary" size="sm" icon={<Cake className="h-3.5 w-3.5" />} onClick={() => setModal('birthday')}>
              Birthdays
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<CalendarHeart className="h-3.5 w-3.5" />}
              onClick={() => setModal('anniversary')}
            >
              Anniversaries
            </Button>
          </div>
        }
      />

      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or phone"
          aria-label="Search people by name or phone"
          className="w-full rounded-md border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-sm text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-accent focus:outline-none"
        />
      </label>

      <div role="tablist" aria-label="Who to show" className="flex flex-wrap gap-1.5">
        {DIRECTORY_FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
                active
                  ? 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:text-tcs-text'
              }`}
            >
              {f.key === 'no_phone' && <PhoneOff className="h-3 w-3" />}
              <span>{f.label}</span>
              <span className={`rounded-full px-1.5 text-[10px] ${active ? 'bg-tcs-raised/20' : 'bg-tcs-surface'}`}>
                {loading ? '…' : counts[f.key]}
              </span>
            </button>
          );
        })}
      </div>

      {filter === 'no_phone' && !loading && (
        <p className="text-xs text-tcs-text-secondary">
          Hourly workers on the roster with no mobile number on file. Until a number is entered on
          their card, no clock-in code can be issued to them.
        </p>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<Users className="h-12 w-12" strokeWidth={1.5} />}
          title={search ? 'Nobody matches that search' : 'Nobody here'}
          body={search ? 'Try a different spelling, or the last digits of their phone.' : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-tcs-border">
            {shown.map((p) => (
              <PersonRowView key={p.group} person={p} />
            ))}
          </ul>
        </Card>
      )}

      {createOpen && (
        <EmployeeCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            toast.success('Employee created');
            refetch();
          }}
        />
      )}
      {modal && <BirthdaysModal mode={modal} onClose={() => setModal(null)} />}
    </div>
  );
}

export function personCardPath(group: string): string {
  return `/people/directory/${encodeURIComponent(group)}`;
}

function PersonRowView({ person }: { person: Person }) {
  return (
    <li>
      <Link
        to={personCardPath(person.group)}
        className="flex items-center gap-3 px-3 py-2.5 hover:bg-tcs-surface"
      >
        <Avatar name={person.name} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-tcs-text">{person.name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1">
            {person.hats.map((h) => (
              <HatChip key={h.person_key ?? `${h.source}:${h.record_id}`} hat={h} />
            ))}
          </span>
        </span>
        <span className="shrink-0 text-xs text-tcs-text-muted">
          {person.hasPhone ? (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {person.hats.find((h) => h.phone)?.phone}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-st-warn-bg px-2 py-0.5 text-st-warn-fg-strong">
              <PhoneOff className="h-3 w-3" /> No phone
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

const TYPE_TONE: Record<PersonType, string> = {
  salaried: 'bg-st-info-bg text-st-info-fg-strong',
  hourly: 'bg-st-ok-bg text-st-ok-fg-strong',
  '1099': 'bg-tcs-surface text-tcs-text-secondary',
  sub_owner: 'bg-tcs-surface text-tcs-text-secondary',
};

export function HatChip({ hat }: { hat: PersonRow }) {
  const type = (hat.person_type ?? 'hourly') as PersonType;
  const label = PERSON_TYPE_LABELS[type] ?? type;
  const detail =
    type === 'sub_owner' && hat.company_name ? ` · ${hat.company_name}` : hat.title ? ` · ${hat.title}` : '';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] ${hat.is_separated ? 'bg-tcs-border text-tcs-text-secondary line-through' : TYPE_TONE[type]}`}
      title={`${label} — ${hat.record_id}`}
    >
      {label}
      {detail}
      {hat.is_separated ? ' · separated' : ''}
    </span>
  );
}
